import { createFileRoute } from "@tanstack/react-router"

import { CodeBlock } from "@/components/dev/CodeBlock"
import { PropsTable } from "@/components/dev/PropsTable"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"

export const Route = createFileRoute("/qr/$qrLookupID/dev/atoms/typography")({
  staticData: {
    pageName: "Typography Components",
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <>
      <style>{`
        .dev-container > *:not(:first-child) {
          margin-top: var(--space-5);
        }
      `}</style>
      <div className="dev-container">
        <Card>
          <Heading level={1} as={2}>
            Typography Components
          </Heading>
          <Text>
            The typography system consists of two main components: Heading and
            Text. Together they create a consistent visual hierarchy and
            semantic HTML structure throughout the application.
          </Text>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Heading Component
          </Heading>
          <Text>
            The Heading component provides semantic headings (h1-h6) with
            consistent typography styles. It separates visual hierarchy (via{" "}
            <code>level</code>) from semantic HTML structure (via{" "}
            <code>as</code>
            ).
          </Text>

          <div>
            <Text level={2} className="semi-bold">
              All Heading Levels
            </Text>
            <Heading level={1}>Heading Level 1</Heading>
            <Heading level={2}>Heading Level 2</Heading>
            <Heading level={3}>Heading Level 3</Heading>
            <Heading level={4}>Heading Level 4</Heading>
            <Heading level={5}>Heading Level 5</Heading>
            <Heading level={6}>Heading Level 6</Heading>
            <CodeBlock>{`<Heading level={1}>Heading Level 1</Heading>
<Heading level={2}>Heading Level 2</Heading>
<Heading level={3}>Heading Level 3</Heading>
<Heading level={4}>Heading Level 4</Heading>
<Heading level={5}>Heading Level 5</Heading>
<Heading level={6}>Heading Level 6</Heading>`}</CodeBlock>
          </div>

          <div>
            <Text level={2} className="semi-bold">
              Semantic HTML vs Visual Style
            </Text>
            <Text level={3}>
              Use the <code>as</code> prop to control the HTML tag while keeping
              the visual style
            </Text>
            <Heading level={1} as={2}>
              I'm an h2 element, styled like h1
            </Heading>
            <CodeBlock>{`<Heading level={1} as={2}>
  I'm an h2 element, styled like h1
</Heading>`}</CodeBlock>
            <Text level={3}>
              Inspect this element - it renders as <code>&lt;h2&gt;</code> but
              has the <code>.headline--1</code> class for styling
            </Text>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Text Component
          </Heading>
          <Text>
            The Text component handles body text, paragraphs, and inline text.
            It provides three size levels plus a special disclaimer level.
          </Text>

          <div>
            <Text level={2} className="semi-bold">
              Text Levels
            </Text>
            <Text level={1}>
              Text Level 1 - Default body text size, used for main content
            </Text>
            <Text level={2}>
              Text Level 2 - Smaller text, used for secondary information
            </Text>
            <Text level={3}>
              Text Level 3 - Smallest text, used for captions and supporting
              details
            </Text>
            <Text level="disclaimer">
              Disclaimer Level - Special styling for legal text and disclaimers
            </Text>
            <CodeBlock>{`<Text level={1}>Text Level 1 - Default size</Text>
<Text level={2}>Text Level 2 - Smaller</Text>
<Text level={3}>Text Level 3 - Smallest</Text>
<Text level="disclaimer">Disclaimer text</Text>`}</CodeBlock>
          </div>

          <div>
            <Text level={2} className="semi-bold">
              Paragraph vs Span
            </Text>
            <Text level={3}>
              Use <code>as="span"</code> for inline text that shouldn't be a
              block element
            </Text>
            <Text>This is a paragraph (default)</Text>
            <div>
              This div contains <Text as="span">an inline span</Text> of text.
            </div>
            <CodeBlock>{`<Text>This is a paragraph (default)</Text>

<div>
  This div contains <Text as="span">an inline span</Text> of text.
</div>`}</CodeBlock>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Typography Hierarchy
          </Heading>
          <Text>
            Visual demonstration of all typography levels together to see how
            they work as a system
          </Text>
          <div>
            <Heading level={1}>Page Title (Heading 1)</Heading>
            <Text level={1}>
              This is the main body text. It's the most common size you'll use
              for paragraphs and general content throughout the application.
            </Text>
            <Heading level={2}>Section Heading (Heading 2)</Heading>
            <Text level={2}>
              This is secondary text. Use it for less important information or
              to create visual hierarchy within a section.
            </Text>
            <Heading level={3}>Subsection Heading (Heading 3)</Heading>
            <Text level={3}>
              This is tertiary text. Great for captions, timestamps, or small
              supporting details.
            </Text>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Common Patterns
          </Heading>

          <div>
            <Text level={2} className="semi-bold">
              Page Title
            </Text>
            <Text level={3}>
              Use level 1 heading as h2 to preserve h1 for site title
            </Text>
            <Heading level={1} as={2}>
              Welcome to Tap N Samba
            </Heading>
            <CodeBlock>{`<Heading level={1} as={2}>
  Welcome to Tap N Samba
</Heading>`}</CodeBlock>
          </div>

          <div>
            <Text level={2} className="semi-bold">
              Card with Title and Description
            </Text>
            <Card>
              <Heading level={2} as={3}>
                Parking Session Active
              </Heading>
              <Text level={1}>Your session ends at 5:00 PM</Text>
              <Text level={3}>Location: Airport Lot A, Space 42</Text>
            </Card>
            <CodeBlock>{`<Card>
  <Heading level={2} as={3}>Parking Session Active</Heading>
  <Text level={1}>Your session ends at 5:00 PM</Text>
  <Text level={3}>Location: Airport Lot A, Space 42</Text>
</Card>`}</CodeBlock>
          </div>

          <div>
            <Text level={2} className="semi-bold">
              Centered Text
            </Text>
            <Heading level={2} className="center">
              Centered Heading
            </Heading>
            <Text className="center">Centered paragraph text</Text>
            <CodeBlock>{`<Heading level={2} className="center">
  Centered Heading
</Heading>
<Text className="center">Centered paragraph text</Text>`}</CodeBlock>
          </div>

          <div>
            <Text level={2} className="semi-bold">
              Bold Text
            </Text>
            <Text>
              Use the{" "}
              <Text as="span" className="semi-bold">
                semi-bold
              </Text>{" "}
              or{" "}
              <Text as="span" className="bold">
                bold
              </Text>{" "}
              utility classes for emphasis
            </Text>
            <CodeBlock>{`<Text>
  Use the <Text as="span" className="semi-bold">semi-bold</Text> or{" "}
  <Text as="span" className="bold">bold</Text> utility classes
</Text>`}</CodeBlock>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Heading Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "level",
                type: "1 | 2 | 3 | 4 | 5 | 6",
                default: "1",
                description:
                  "Visual style level - applies .headline--{level} class",
              },
              {
                name: "as",
                type: "1 | 2 | 3 | 4 | 5 | 6",
                default: "Same as level",
                description:
                  "Semantic HTML tag (h1-h6) - allows visual style to differ from semantic meaning",
              },
              {
                name: "className",
                type: "string",
                default: "undefined",
                description:
                  'Additional CSS classes (e.g., "center", "semi-bold")',
              },
              {
                name: "children",
                type: "ReactNode",
                default: "undefined",
                description: "Heading text content",
              },
            ]}
          />
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Text Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "level",
                type: '1 | 2 | 3 | "disclaimer"',
                default: "1",
                description: "Text size level - applies .body--{level} class",
              },
              {
                name: "as",
                type: '"p" | "span"',
                default: '"p"',
                description:
                  "HTML tag - use 'span' for inline text, 'p' for paragraphs",
              },
              {
                name: "className",
                type: "string",
                default: "undefined",
                description:
                  'Additional CSS classes (e.g., "center", "semi-bold", "bold")',
              },
              {
                name: "children",
                type: "ReactNode",
                default: "undefined",
                description: "Text content",
              },
            ]}
          />
        </Card>
      </div>
    </>
  )
}
